import { useState, useEffect, useRef } from 'react';
import { findIndex } from 'lodash';

import {
  getCompanies,
  getLegalEntities,
  getAllProperties,
  getProperties,
  getAllAssessors
} from '../../services';
import {
  Company,
  ILegalEntity,
  Property,
  AssessorAccount,
  SelectOption,
  State
} from '../../interface';
import useSelectedSystemTaxYear from '../useSelectedSystemTaxYear';

import { IBulkEditData, IUseBulkEditDataProps, TToggleBulkEditData, TIsLoading } from './types';

export const useBulkEditData = ({ isSelected, states }: IUseBulkEditDataProps) => {
  const [isLoading, setIsLoading] = useState<TIsLoading>({} as TIsLoading);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesOptions, setCompaniesOptions] = useState<SelectOption[]>([]);
  const [legalEntities, setLegalEntities] = useState<ILegalEntity[]>([]);
  const [statesOptions, setStatesOptions] = useState<SelectOption[]>([]);
  const [legalEntitiesOptions, setLegalEntitiesOptions] = useState<SelectOption[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [propertiesOptions, setPropertiesOptions] = useState<SelectOption[]>([]);
  const [assessorAccounts, setAssessorAccounts] = useState<AssessorAccount[]>([]);
  const [assessorAccountsOptions, setAssessorAccountsOptions] = useState<SelectOption[]>([]);

  const [bulkEditData, setBulkEditData] = useState<IBulkEditData>({} as IBulkEditData);
  const [toggleBulkEditData, setToggleBulkEditData] = useState<TToggleBulkEditData>({
    addition: false,
    disposed: false
  });

  const legalEntitiesDataRef = useRef<ILegalEntity[]>([]);
  const propertiesDataRef = useRef<Property[]>([]);
  const assessorAccountsDataRef = useRef<AssessorAccount[]>([]);

  const selectedSystemTaxYear = useSelectedSystemTaxYear();
  const taxYear = selectedSystemTaxYear.taxYear;

  const getPropertiesByLegalEntitiesIds = async (legalEntities: ILegalEntity[]) => {
    const legalEntitiesIds = legalEntities.map((legalEntity: ILegalEntity) => legalEntity.id);
    let propertiesList: Property[] = [];

    if (!legalEntitiesIds.length) {
      setProperties(propertiesDataRef.current);
      return [];
    }

    try {
      setIsLoading((prevData) => ({
        ...prevData,
        property: true
      }));
      const property = await getProperties({
        legalEntityIds: legalEntitiesIds.toString()
      });
      propertiesList = property.data?.items || [];
      if (propertiesList.length) {
        setProperties(propertiesList);
      } else {
        setProperties(propertiesDataRef.current);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading((prevData) => ({
        ...prevData,
        property: false
      }));
    }
    return propertiesList;
  }

  const getAssessorAccountsByPropertyIds = async (properties: Property[]) => {
    const propertiesIds = properties.map((property: Property) => property.id);

    if (!propertiesIds.length) {
      setAssessorAccounts(assessorAccountsDataRef.current);
      return;
    }

    try {
      setIsLoading((prevData) => ({
        ...prevData,
        assessorAccount: true
      }));
      const assessor = await getAllAssessors(taxYear, propertiesIds.toString());
      const assessorList = assessor.data?.items || [];
      if (assessorList.length) {
        setAssessorAccounts(assessorList);
      } else {
        setAssessorAccounts(assessorAccountsDataRef.current);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading((prevData) => ({
        ...prevData,
        assessorAccount: false
      }));
    }
  }

  useEffect(() => {
    (async () => {
      if (!isSelected) return;

      try {
        setIsLoading((prevData) => ({
          company: true,
          legalEntity: true,
          property: true,
          assessorAccount: true
        }));
        const company = await getCompanies();
        if (company.data?.items) {
          const activeCompanies = company.data.items.filter((company: Company) => company.active);
          setCompanies(activeCompanies);
        }

        const legalEntity = await getLegalEntities();
        if (legalEntity.data?.items) {
          setLegalEntities(legalEntity.data?.items);
          legalEntitiesDataRef.current = legalEntity.data?.items;
        }

        const property = await getAllProperties();
        if (property.data?.items) {
          setProperties(property.data.items);
          propertiesDataRef.current = property.data.items;
        }

        const assessor = await getAllAssessors(taxYear);
        if (assessor.data?.items) {
          setAssessorAccounts(assessor.data.items);
          assessorAccountsDataRef.current = assessor.data.items;
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading({
          company: false,
          legalEntity: false,
          property: false,
          assessorAccount: false
        });
      }
    })();
  }, [isSelected]);

  useEffect(() => {
    if (companies?.length) {
      const companyOptions = companies.map((company: Company) => ({
        label: `${company.name} - ${company.number}`,
        value: company.id
      }));
      setCompaniesOptions(companyOptions);
    }
  }, [companies]);

  useEffect(() => {
    if (!bulkEditData.company?.id) return;

    (async () => {
      setIsLoading((prevData) => ({
        ...prevData,
        legalEntity: true
      }));
      try {
        const legalEntity = await getLegalEntities({
          companyId: Number(bulkEditData.company?.id) || 0
        });
        const legalEntitiesItems = legalEntity.data?.items?.length
          ? legalEntity.data.items
          : null;

        setLegalEntities(legalEntitiesItems || legalEntitiesDataRef.current);

        const propertiesList = await getPropertiesByLegalEntitiesIds(
          legalEntitiesItems || []
        );
        getAssessorAccountsByPropertyIds(propertiesList);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading((prevData) => ({
          ...prevData,
          legalEntity: false
        }));
      }
    })();
  }, [bulkEditData.company?.id]);

  useEffect(() => {
    if (legalEntities?.length) {
      const legalEntitiesOptions = legalEntities.map((legalEntity: ILegalEntity) => ({
        label: `${legalEntity.name} - ${legalEntity.number}`,
        value: legalEntity.id as number
      }));
      setLegalEntitiesOptions(legalEntitiesOptions);
    }
  }, [legalEntities]);

  useEffect(() => {
    if (!bulkEditData.legalEntity?.id) return;

    (async () => {
      const propertiesList = await getPropertiesByLegalEntitiesIds(
        [bulkEditData.legalEntity]
      );
      getAssessorAccountsByPropertyIds(propertiesList);
    })();
  }, [bulkEditData.legalEntity?.id]);

  useEffect(() => {
    if (properties?.length) {
      const propertyOptions = properties.map((property: Property) => ({
        label: property.number,
        value: property.id
      }));
      setPropertiesOptions(propertyOptions);
    }
  }, [properties]);

  useEffect(() => {
    if (!bulkEditData.property?.id) return;
    getAssessorAccountsByPropertyIds([bulkEditData.property]);
  }, [bulkEditData.property?.id]);

  useEffect(() => {
    if (assessorAccounts?.length) {
      const assessorOptions = assessorAccounts.map((assessor: AssessorAccount) => ({
        label: assessor.number,
        value: assessor.id
      }));
      setAssessorAccountsOptions(assessorOptions);
    }
  }, [assessorAccounts]);

  useEffect(() => {
    if (states?.length) {
      const stateOptions = states.map((state: State) =>
      ({
        label: state.abbreviation,
        value: state.id
      })
      );
      setStatesOptions(stateOptions);
    }
  }, [states]);

  const onCancelBulkEditData = () => {
    setBulkEditData({} as IBulkEditData);
    setToggleBulkEditData({
      addition: false,
      disposed: false,
    });
    setCompaniesOptions([...companiesOptions]);
    setStatesOptions([...statesOptions]);

    setCompanies(companies);  
    setLegalEntities(legalEntitiesDataRef.current); 
    setProperties(propertiesDataRef.current); 
    setAssessorAccounts(assessorAccountsDataRef.current); 
    setStatesOptions([...statesOptions]); 
  };

  const onToggleSwitches = (key: 'addition' | 'disposed') => {
    setToggleBulkEditData((prevData) => ({
      ...prevData,
      [key]: !toggleBulkEditData[key]
    }));
  };

  const onSelectValue = (key: keyof IBulkEditData, option: SelectOption) => {
    if (option?.value) {
      const listMapping: { [key in keyof IBulkEditData]: any[] } = {
        company: companies,
        legalEntity: legalEntities,
        state: states,
        property: properties,
        assessorAccount: assessorAccounts,
      }
      const itemId = option.value as string;
      const itemIndex = findIndex(listMapping[key], { 'id': Number(itemId) });


      if (key === 'property') {
        const selectedProperty = listMapping[key][itemIndex] as Property;
        setBulkEditData((prevData) => ({
          ...prevData,
          property: selectedProperty,
          ...(selectedProperty?.company ? {
            company: selectedProperty.company as Company  
          } : {}),
          ...(selectedProperty?.legalEntity ? {
            legalEntity: selectedProperty.legalEntity
          } : {}),
          ...(selectedProperty?.address?.state ? {
            state: selectedProperty.address.state
          } : {})
        }));
      } else {
        setBulkEditData((prevData) => ({
          ...(
            key !== 'company'
              ? prevData
              : {} as IBulkEditData
          ),
          [key]: listMapping[key][itemIndex]
        }));
      }
    } else {
      setBulkEditData((prevData) => ({
        ...prevData,
        [key]: {} as IBulkEditData
      }));
    }
  };

  return {
    isLoading,
    companiesOptions,
    legalEntitiesOptions,
    propertiesOptions,
    assessorAccountsOptions,
    statesOptions,
    bulkEditData,
    toggleBulkEditData,
    onCancelBulkEditData,
    onToggleSwitches,
    onSelectValue,
  };
};

export type { IBulkEditData };
export default useBulkEditData;